import { getAuthTokensFromLocalStorage, QueryData } from 'lib/api/utils';
import { useFetchMe } from 'lib/hooks/user/user.queries';
import React, { PropsWithChildren } from 'react';
import { UserRole } from 'types/shared';

type AuthContextState = {
  user: QueryData<typeof useFetchMe>['user'] | null;
  isAdmin: () => boolean;
  isImpersonating: () => boolean;
};

type AuthContextActions = {};

/**
 * We declared State and Actions type separately for better organization, here we join them together and add any other property types we want to add to the context
 */
type AuthContextType = AuthContextState & AuthContextActions & {};

const AuthContext = React.createContext<AuthContextType | null>(null);

/**
 * Custom hook to access the app auth context
 *
 * @throws {Error} If used outside of AuthContextProvider
 *
 * @returns The auth context value
 */
function useAuthContext(): AuthContextState {
  const context = React.useContext(AuthContext);

  if (!context)
    throw new Error('useAuthContext must be used within a AuthContextProvider');

  return context;
}

/**
 * Provider component for the AuthContext, should usually be used in the top most component of the application
 */
function AuthContextProvider({ children }: PropsWithChildren<{}>) {
  const { data: userData } = useFetchMe();

  const user = userData?.user ?? null;
  /**
   * getter function to check if the user is an admin
   *
   * @returns true if the user role is one of the admin roles, false otherwise
   *
   * @description
   * The admin roles are: SUPER_ADMIN, SYSOPS, ADMIN
   */
  const isAdmin = () =>
    [UserRole.SUPER_ADMIN, UserRole.SYSOPS, UserRole.ADMIN].includes(
      user?.role as UserRole
    );

  /**
   * getter function to check if the admin is impersonating
   *
   * @returns true if the admin is impersonating, false otherwise
   *
   * @description
   * The admin is impersonating if the admin token is not null i.e there are 2 tokens in the local storage
   */
  const isImpersonating = () =>
    getAuthTokensFromLocalStorage().adminToken !== null;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;

export { AuthContextProvider, useAuthContext };
